import { useEffect, useRef } from "react";
import type { Identifier } from "ra-core";
import { Form, required, useGetList, useNotify, useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { contactOptionText } from "@/components/atomic-crm/misc/ContactOption";
import { useTeam } from "@/tenax/tasks/taskData";
import {
  AGREEMENT_TYPES,
  RENEWAL_MODES,
  SAVED_STATUSES,
  packageDefaults,
  toSaveArgs,
  useKitVersions,
  usePackages,
  type Agreement,
  type Package,
  type FreeItem,
  type Obligation,
} from "./agreementData";

const field = (name: string) => `resources.agreements.fields.${name}`;
const choicesOf = (prefix: string, values: readonly string[]) =>
  values.map((id) => ({ id, name: `tenax.agreements.${prefix}.${id}` }));

export interface AgreementFormValues extends Partial<Agreement> {
  obligations?: Partial<Obligation>[];
  free_items?: Partial<FreeItem>[];
}

interface Named {
  id: number;
  name: string;
}

// Everything the form chooses from, loaded before the form shows: a menu
// opened while a list was still loading closed again (D-039)
const useAgreementLookups = () => {
  const params = {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" as const },
  };
  const team = useTeam();
  const packages = usePackages();
  const kits = useKitVersions();
  const priceLists = useGetList<Named>("price_lists", params);
  const rules = useGetList<Named>("commission_rules", params);
  const all = [team, packages, kits, priceLists, rules];
  return {
    isPending: all.some((query) => query.isPending),
    team: team.data ?? [],
    packages: packages.data ?? [],
    kits: kits.data ?? [],
    priceLists: priceLists.data ?? [],
    rules: rules.data ?? [],
  };
};

type Lookups = ReturnType<typeof useAgreementLookups>;

const FormSkeleton = () => {
  const translate = useTranslate();
  return (
    <div
      role="status"
      aria-label={translate("ra.page.loading")}
      className="flex flex-col gap-4"
    >
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
};

// The whole agreement on one screen (task 1.10, D-039): only type, title
// and one party are needed (D-031). Saved by tenax_save_agreement with its
// obligations and free items in one transaction.
export const AgreementForm = ({
  id,
  defaultValues,
  onSaved,
}: {
  id: Identifier | null;
  defaultValues: AgreementFormValues;
  onSaved: (id: number) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const lookups = useAgreementLookups();

  const save = async (values: AgreementFormValues) => {
    if (values.company_id == null && values.contact_id == null) {
      notify("tenax.agreements.need_party", { type: "error" });
      return;
    }
    const { data, error } = await getSupabaseClient().rpc(
      "tenax_save_agreement",
      toSaveArgs(id, values),
    );
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    await Promise.all(
      [
        "agreements",
        "agreement_checks",
        "agreement_first_order",
        "agreement_obligations",
        "agreement_free_items",
      ].map((resource) =>
        queryClient.invalidateQueries({ queryKey: [resource] }),
      ),
    );
    notify("tenax.agreements.saved", { type: "info" });
    onSaved(Number(data));
  };

  if (lookups.isPending) return <FormSkeleton />;
  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={(values: Record<string, unknown>) =>
        save(values as AgreementFormValues)
      }
    >
      <AgreementInputs lookups={lookups} />
      <div className="mt-6 flex justify-end">
        <Button type="submit">{translate("ra.action.save")}</Button>
      </div>
    </Form>
  );
};

const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
);

const AgreementInputs = ({ lookups }: { lookups: Lookups }) => {
  const translate = useTranslate();
  const { team, packages, kits, priceLists, rules } = lookups;
  usePackagePrefill(packages);
  const packageId: number | undefined = useWatch({ name: "package_id" });
  const kitId: number | undefined = useWatch({ name: "kit_version_id" });

  return (
    <div className="flex flex-col gap-4">
      <Row>
        <SelectInput
          source="type"
          label={field("type")}
          choices={choicesOf("type", AGREEMENT_TYPES)}
          validate={required()}
          helperText={false}
        />
        <SelectInput
          source="status"
          label={field("status")}
          choices={choicesOf("status", SAVED_STATUSES)}
          helperText={false}
        />
      </Row>
      <TextInput
        source="title"
        label={field("title")}
        validate={required()}
        helperText={false}
      />
      <Row>
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteInput
            label={field("company_id")}
            optionText="name"
            helperText={false}
          />
        </ReferenceInput>
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput
            label={field("contact_id")}
            optionText={contactOptionText}
            helperText={false}
          />
        </ReferenceInput>
      </Row>
      <Row>
        <SelectInput
          source="sales_id"
          label={field("sales_id")}
          choices={team.map((member) => ({
            id: member.id,
            name: `${member.first_name} ${member.last_name}`,
          }))}
          helperText={false}
        />
        <ReferenceInput
          source="introduced_by_contact_id"
          reference="contacts_summary"
        >
          <AutocompleteInput
            label={field("introduced_by_contact_id")}
            optionText={contactOptionText}
            helperText={false}
          />
        </ReferenceInput>
      </Row>

      <Separator />
      <Row>
        <DateInput
          source="signed_at"
          label={field("signed_at")}
          helperText={false}
        />
        <DateInput
          source="start_date"
          label={field("start_date")}
          helperText={false}
        />
        <DateInput
          source="end_date"
          label={field("end_date")}
          helperText={false}
        />
        <SelectInput
          source="renewal"
          label={field("renewal")}
          choices={choicesOf("renewal", RENEWAL_MODES)}
          helperText={false}
        />
        <NumberInput
          source="notice_days"
          label={field("notice_days")}
          helperText={false}
        />
      </Row>

      <Separator />
      <Row>
        <SelectInput
          source="package_id"
          label={field("package_id")}
          choices={packages.filter((pkg) => pkg.active || pkg.id === packageId)}
          optionText="name"
          helperText={false}
        />
        <NumberInput
          source="contribution_amount"
          label={field("contribution_amount")}
          helperText={false}
        />
        <SelectInput
          source="kit_version_id"
          label={field("kit_version_id")}
          choices={kits.filter((kit) => kit.active || kit.id === kitId)}
          optionText="name"
          helperText={false}
        />
        <DateInput
          source="kit_shipped_at"
          label={field("kit_shipped_at")}
          helperText={false}
        />
      </Row>

      <Separator />
      <Row>
        <NumberInput
          source="min_purchase_amount"
          label={field("min_purchase_amount")}
          helperText={false}
        />
        <DateInput
          source="min_purchase_deadline"
          label={field("min_purchase_deadline")}
          helperText={false}
        />
        <SelectInput
          source="price_list_id"
          label={field("price_list_id")}
          choices={priceLists}
          optionText="name"
          helperText={false}
        />
        <SelectInput
          source="commission_rule_id"
          label={field("commission_rule_id")}
          choices={rules}
          optionText="name"
          helperText={false}
        />
        <NumberInput
          source="customer_discount_pct"
          label={field("customer_discount_pct")}
          helperText={false}
        />
        <NumberInput
          source="referrer_reward_pct"
          label={field("referrer_reward_pct")}
          helperText={false}
        />
      </Row>

      <Separator />
      <h3 className="text-sm font-semibold">
        {translate(field("obligations"))}
      </h3>
      <ArrayInput source="obligations" label={false} helperText={false}>
        <SimpleFormIterator disableReordering disableClear inline>
          <SelectInput
            source="party"
            label="tenax.agreements.party.counterparty"
            choices={choicesOf("party", ["tenax", "counterparty"])}
            helperText={false}
          />
          <TextInput
            source="description"
            label="tenax.agreements.obligation"
            helperText={false}
          />
          <DateInput
            source="due_date"
            label="tenax.agreements.due_date"
            helperText={false}
          />
          <DateInput
            source="done_at"
            label="tenax.agreements.done_at"
            helperText={false}
          />
        </SimpleFormIterator>
      </ArrayInput>

      <h3 className="text-sm font-semibold">
        {translate(field("free_items"))}
      </h3>
      <ArrayInput source="free_items" label={false} helperText={false}>
        <SimpleFormIterator disableReordering disableClear inline>
          <TextInput
            source="description"
            label="tenax.agreements.item"
            helperText={false}
          />
          <NumberInput
            source="qty"
            label="tenax.agreements.qty"
            helperText={false}
          />
          <NumberInput
            source="unit_value"
            label="tenax.agreements.unit_value"
            helperText={false}
          />
        </SimpleFormIterator>
      </ArrayInput>

      <Separator />
      <TextInput
        source="terms"
        label={field("terms")}
        multiline
        helperText={false}
      />
      <TextInput
        source="drive_url"
        label={field("drive_url")}
        helperText={false}
      />
    </div>
  );
};

// Choosing a package prefills its starting values where the form is empty
const usePackagePrefill = (packages: Package[]) => {
  const { getValues, setValue } = useFormContext();
  const packageId: number | undefined = useWatch({ name: "package_id" });
  const previous = useRef(packageId);
  useEffect(() => {
    if (packageId === previous.current) return;
    previous.current = packageId;
    const pkg = packages.find((item) => item.id === packageId);
    Object.entries(packageDefaults(getValues(), pkg)).forEach(([name, value]) =>
      setValue(name, value, { shouldDirty: true }),
    );
  }, [packageId, packages, getValues, setValue]);
};
